/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnDestroy, OnInit, VERSION } from "@angular/core";
import {
  BoardProvider,
  QuoteProvider,
  ReportProvider,
  HolidayProvider,
} from "../../../providers";
import { HolidayDates } from "../../../model/helper/holiday-dates";
import { Router } from "@angular/router";
import { RecentReport } from "../../../model/report";
import {
  ModeService,
  BoardService,
  RecentReportService,
  StageService,
  UserService,
  ThemeService,
  LogService,
} from "../../../services";
import { Board, Helpers, User, Theme } from "../../../model";
import { CreateNewBoardModal, SecurityNoticeModal } from "../../modal";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { Subject } from "rxjs";
import { SystemType } from "src/app/model/enums/system.type";
import { ConfirmationDialogComponent } from "../../fields";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as fs from "wicg-file-system-access";
import { ReportService } from "src/app/services/report.service";
import * as _ from "lodash";
import { RmaParsarComponent } from "src/app/modules/manage/components/rma-parsar/rma-parsar.component";
import { RotParsarComponent } from "src/app/modules/manage/components/rot-parsar/rot-parsar.component";
import { BizCompsParserComponent } from "src/app/modules/manage/components/bizcomps-parser/bizcomps-parser.component";
import { DealStateParserComponent } from "src/app/modules/manage/components/dealstate-parser/dealstate-parser.component";
@Component({
  selector: "page-home",
  templateUrl: "home.page.html",
  styleUrls: ["home.page.scss"],
})
export class HomePage implements OnInit, OnDestroy {
  angularVersion: string;

  currentUser: User;
  private currentUserSub: any;

  private currentUserProfilePhotoSub: any;

  boards: Board[] = [];
  defaultBoard: Board = new Board();
  private boardsSub: any;

  recentReports: RecentReport[];
  private recentSub: any;

  chromeAppInstalled: boolean;
  private chromeAppSub: any;
  createModal: BsModalRef;

  currentMode: SystemType;
  private modeSubscription: any;
  Mode = SystemType;
  deleteModal: BsModalRef;
  modalRef: BsModalRef;
  rmaModal: BsModalRef;
  roadModel: BsModalRef;
  bizcompsModel: BsModalRef;
  dealstatModel: BsModalRef;

  userSub: any;

  totalQuotes: number = 0;
  quoteSub: any;
  avatarLoading: boolean = false;
  currentYear = new Date().getFullYear();

  constructor(
    private router: Router,
    public userService: UserService,
    private reportProvider: ReportProvider,
    private stageService: StageService,
    private recentReportsService: RecentReportService,
    private modeService: ModeService,
    private modalService: BsModalService,
    private boardService: BoardService,
    private boardProvider: BoardProvider,
    private themeService: ThemeService,
    private quoteProvider: QuoteProvider,
    private reportService: ReportService,
    private logService: LogService,
    private holidayProvider: HolidayProvider
  ) {
    this.angularVersion = VERSION.full;
  }

  ngOnInit(): void {
    // Load admin-managed holiday dates (single source of truth) for all business-day
    // calculations: report due dates, engagement-letter completion estimates, the
    // due-date calendar and dashboard day diffs.
    this.holidayProvider.getDates().subscribe(
      (data) => {
        if (data.success) HolidayDates.set(data.result);
      },
      (err) => this.logService.log(err)
    );

    this.currentUserSub = this.userService.user$.subscribe((currentUser) => {
      this.currentUser = currentUser;
      if (this.currentUser && this.currentUser.AVATAR_URL) {
        this.userService.setUserProfilePhoto(this.currentUser.AVATAR_URL);
      }
      // theme color settings
      if (this.currentUser && this.currentUser.THEME_NIGHT) {
        let theme = new Theme();
        theme.primary_color = "#212121";
        theme.primary_border = "#7656A1";
        theme.background_color = "#212121";
        theme.background_color_2 = "#2E2E2E";
        theme.main_text_color = "#f0f0f0";
        theme.blue_text_color = "#e3e4e4";
        theme.purple_text_color = "#B5B2BD";
        theme.grey_text_color = "#ACACAC";
        theme.purple_hover_color = "#3498db";
        theme.report_row_hover_color = "#251F2D";
        theme.btn_color = "#b5b2bd";
        theme.btn_boder = "#353535";
        theme.section_header_color = "#fff";
        theme.section_header_hover_color = "#fff";
        theme.white_gray_color = "#9fa1a4";
        theme.div_boder = "#9fa1a4";
        theme.override = "#440b28";
        theme.data_selected = "#5b5959";
        theme.dropdown_border_bottom_color = "#bb86fc45";
        theme.avatar_background_color = "#212121";
        theme.avatar_text_color = "#fff";
        theme.avatar_border = "#212121";
        theme.tab_item = "#9a9a9a";
        theme.active_tab_color = "#79589F";
        theme.active_tab_border = "#a996c0";

        theme.input_background = "#333333"; //#c69ef629
        theme.input_disabled = "#495057cf";
        theme.input_border = "#616161 "; // bb86fcd1
        theme.input_active_box_shadow = "rgb(196, 117, 255)";
        theme.input_active_border = "#a200f4fa";
        theme.quill_editor_border = theme.input_border;
        theme.input_data_border = theme.input_border;
        theme.progressbar_background = theme.input_background;
        theme.progressbar_border = theme.input_border;

        this.themeService.setTheme(theme);
        document.documentElement.style.background = theme.primary_color;
      } else {
        let theme = new Theme();
        theme.primary_color = "#f7f8fb";
        theme.primary_border = "#e8ebf3";
        theme.background_color = "#fff";
        theme.background_color_2 = "#f7f8fc4d";
        theme.main_text_color = "#212529";
        theme.blue_text_color = "#1a3b6c";
        theme.purple_text_color = "#337ab7";
        theme.grey_text_color = "#9a9a9a";
        theme.purple_hover_color = "#61467f";
        theme.report_row_hover_color = "#E7EBFA";
        theme.btn_color = "#79589F";
        theme.btn_boder = "#79589f99";
        theme.section_header_color = "#1e3762";
        theme.section_header_hover_color = "#1d5587";
        theme.white_gray_color = "#f4f5f7";
        theme.div_boder = "#5969804d";
        theme.override = "#fbe8e8";
        theme.data_selected = "#f8f9fc";
        theme.dropdown_border_bottom_color = "#EEF1F6";
        theme.avatar_background_color = "#f8f9fc";
        theme.avatar_text_color = "#252a2d";
        theme.avatar_border = "eaeef5";
        theme.tab_item = "#96a3b6";
        theme.active_tab_color = "#79589F";
        theme.active_tab_border = "#a996c0";

        theme.input_background = "#fff";
        theme.input_disabled = "#f7f8fc";
        theme.input_border = "#ced4da";
        theme.input_active_box_shadow = "rgba(102, 175, 233, 0.6)";
        theme.input_active_border = "#80bdff";
        theme.quill_editor_border = "#ccc";
        theme.input_data_border = "#d4d4d4";
        theme.progressbar_background = "#f7f8fb";
        theme.progressbar_border = "#d8cce6";

        this.themeService.setTheme(theme);
        document.documentElement.style.background = "";
      }
    });

    this.modeSubscription = this.modeService.mode$.subscribe((mode) => {
      this.currentMode = mode;
      this.countQuotes();
    });

    if (this.currentUser == null) {
      this.userService.init();
    }
    this.boardsSub = this.boardService.boards$.subscribe((boards) => {
      if (boards) {
        this.boards = boards;
        this.boards.forEach((b) => {
          if (b.IS_DEFAULT) {
            this.defaultBoard = b;
          }
        });
      }
    });

    this.recentSub = this.recentReportsService.recentReports$.subscribe(
      (reports) => (this.recentReports = reports)
    );
    this.chromeAppSub = this.userService.extensionState$.subscribe(
      (extState) => (this.chromeAppInstalled = extState)
    );
    this.checkFSApi();
    this.getUserBoards();

    if (localStorage.hasOwnProperty("R-CRM-SYSTEM_MODE")) {
      this.currentMode =
        SystemType[<string>localStorage.getItem("R-CRM-SYSTEM_MODE")];
      this.modeService.mode.next(this.currentMode);
    }

    this.userSub = this.userService.onShowSecurityNotice.subscribe(() => {
      if (!this.currentUser.TFA) this.securityNotice();
    });

    this.quoteSub = this.reportService.updateQuote.subscribe(
      () => (this.totalQuotes -= 1)
    );

    this.currentUserProfilePhotoSub =
      this.userService.userProfilePhoto$.subscribe((u) => {
        if (u != undefined && u != null) {
          this.avatarLoading = true;
          this.currentUser.AVATAR_URL = u;

          setTimeout(() => {
            this.avatarLoading = false;
          }, 1000);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.currentUserSub) this.currentUserSub.unsubscribe();
    if (this.recentSub) this.recentSub.unsubscribe();
    if (this.chromeAppSub) this.chromeAppSub.unsubscribe();
    if (this.boardsSub) this.boardsSub.unsubscribe();
    if (this.quoteSub) this.quoteSub.unsubscribe();
    //if(this.userSub) this.userSub.unsubscribe();
    if (this.currentUserProfilePhotoSub)
      this.currentUserProfilePhotoSub.unsubscribe();
  }

  signOut(): void {
    localStorage.removeItem("R-CRM-TOKEN");
    localStorage.removeItem("R-CRM-PROFILE");

    if (this.userSub) this.userSub.unsubscribe();

    // noinspection JSIgnoredPromiseFromCall
    this.router.navigate(["/login"]);
  }

  private checkFSApi(): void {
    try {
      if (typeof window.showOpenFilePicker !== "undefined") {
        this.userService.setExtensionState(true);
        this.logService.log("FS API Enabled");
      } else {
        this.userService.setExtensionState(false);
        this.logService.log("FS API Disabled");
      }
    } catch (e) {
      this.userService.setExtensionState(false);
    }
  }

  getUserBoards(): void {
    this.boardProvider
      .getUserBoards(<string>this.currentMode)
      .subscribe((data) => {
        if (data.success) {
          this.boardService.setBoards(Board.parseArray(data.result));
        }
      });
  }

  createNewBoard(): void {
    this.createModal = this.modalService.show(
      CreateNewBoardModal,
      Helpers.getModalConfig()
    );
    const boardSubject = new Subject<Board>();
    this.createModal.content.subject = boardSubject;
    this.createModal.content.isEdit = false;
    boardSubject.subscribe((value) => {
      this.boards.push(Board.parse(value));
      this.boardService.setBoards(this.boards);
      this.router.navigate(["/boards/" + Board.parse(value).ID]);
    });
  }

  openRma(): void {
    this.rmaModal = this.modalService.show(
      RmaParsarComponent,
      Helpers.getModalConfig()
    );
  }

  openRot(): void {
    this.roadModel = this.modalService.show(
      RotParsarComponent,
      Helpers.getModalConfig()
    );
  }

  openBizcomps(): void {
    this.bizcompsModel = this.modalService.show(
      BizCompsParserComponent,
      Helpers.getModalConfig()
    );
  }

  openDealState(): void {
    this.dealstatModel = this.modalService.show(
      DealStateParserComponent,
      Helpers.getModalConfig()
    );
  }

  switchModes(): void {
    if (this.currentMode === SystemType.EA) this.currentMode = SystemType.BV;
    else if (this.currentMode === SystemType.BV)
      this.currentMode = SystemType.EA;

    localStorage.setItem("R-CRM-SYSTEM_MODE", this.currentMode);

    this.modeService.mode.next(this.currentMode);
    this.router.navigate(["/dashboard"]);
  }

  isEaAccess(): boolean {
    if (this.currentUser) {
      if (this.currentUser.USER_ROLES.length === 0) return false;

      for (const r of this.currentUser.USER_ROLES)
        if (r.ROLE_PRIVILEGES.length > 0) {
          for (const p of r.ROLE_PRIVILEGES) {
            if (p.CODE === "EA_ALL_ACCESS") return true;
          }
        }
    }

    return false;
  }

  isCoreAccess(): boolean {
    return this.userService.isCoreAccess();
  }

  onSetDefault(event: any, board: Board): void {
    event.stopPropagation();
    if (board.IS_DEFAULT) {
      this.boardProvider.unSetDefaultBoard(board).subscribe((data) => {
        if (data.success) {
          Object.assign(board, data.result);
          this.defaultBoard = new Board();
        }
      });
    } else {
      this.boardProvider.setDefaultBoard(board).subscribe((data) => {
        if (data.success) {
          this.boards.forEach((b) => {
            b.IS_DEFAULT = false;
          });
          Object.assign(board, data.result);
          this.defaultBoard = board;
        }
      });
    }
  }

  onDeleteBoard(event: any, board: Board): void {
    event.stopPropagation();
    this.deleteModal = this.modalService.show(
      ConfirmationDialogComponent,
      Helpers.getModalConfig()
    );
    const ackSubject = new Subject<boolean>();
    this.deleteModal.content.text = "Are you sure you want to delete board?";
    this.deleteModal.content.title = "Delete Board";
    this.deleteModal.content.actionButton = "Confirm";
    this.deleteModal.content.subject = ackSubject;

    ackSubject.subscribe((value) => {
      if (value) {
        this.boardProvider.deleteBoard(board.ID).subscribe((data) => {
          if (data.success) {
            this.boards = this.boards.filter((b) => b.ID != board.ID);
            if (board.IS_DEFAULT) {
              this.defaultBoard = new Board();
            }
            this.boardService.setBoards(this.boards);
          }
        });
      }
    });
  }

  isAnyDefaultBoard(): boolean {
    if (
      this.boards.find((board) => {
        if (board.IS_DEFAULT) return board;
      })
    )
      return true;
    else return false;
  }

  // When 2FA was enabled
  securityNotice(): void {
    let value = false;
    if (localStorage.hasOwnProperty("R-CRM-SECURITY_NOTICE")) {
      value =
        localStorage.getItem("R-CRM-SECURITY_NOTICE") == "true" ? true : false;
    }

    if (value == false) {
      this.modalRef = this.modalService.show(
        SecurityNoticeModal,
        Helpers.getModalConfig()
      );
      const ackSubject = new Subject<boolean>();
      this.modalRef.content.subject = ackSubject;

      ackSubject.subscribe((value) => {});
    }
  }

  countQuotes(): void {
    this.quoteProvider.countQuotes(this.currentMode).subscribe(
      (data) => {
        if (data.success) {
          this.totalQuotes = data.result;
        }
      },
      (err) => {
        this.logService.log(err);
      }
    );
  }
}
